/**
 * Aqualumen quick add: the round "+" on snippets/aq-product-card.liquid adds a single-variant
 * product to the cart without leaving the page. After a successful add it dispatches Horizon's
 * standard CartLinesUpdateEvent (like product-form.js) with the refreshed cart and the cart-items
 * section HTML, so the header count, the cart drawer contents and the drawer's auto-open update
 * the usual way. Errors stay on the card and go to the section's live region.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const ERROR_TEXT = 'Nu am putut adăuga produsul în coș. Încearcă din nou.';
const DONE_MS = 1800;
const ERROR_MS = 6000;

function routesRoot() {
  const root = window.Shopify?.routes?.root || '/';
  return root.endsWith('/') ? root : `${root}/`;
}

class AqQuickAdd extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.#controller = new AbortController();
    this.querySelector('button')?.addEventListener('click', this.#onClick, { signal: this.#controller.signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
    clearTimeout(this.#timer);
  }

  get #button() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('button'));
  }

  get #errorEl() {
    return /** @type {HTMLElement | null} */ (this.closest('.aq-card')?.querySelector('[data-aq-quick-error]') ?? null);
  }

  /** @param {string} text */
  #announce(text) {
    const live = this.closest('[data-aq-live-scope]')?.querySelector('[data-aq-live]');
    if (!live) return;
    live.textContent = '';
    requestAnimationFrame(() => (live.textContent = text));
  }

  /** @param {'idle' | 'busy' | 'done' | 'error'} state */
  #setState(state) {
    const button = this.#button;
    this.dataset.state = state;
    if (!button) return;
    // aria-disabled rather than disabled, so keyboard focus stays on the button while it works
    button.setAttribute('aria-busy', String(state === 'busy'));
    button.setAttribute('aria-disabled', String(state === 'busy'));
  }

  /** @param {string} message */
  #showError(message) {
    this.#setState('error');
    const error = this.#errorEl;
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    this.#announce(message);
    clearTimeout(this.#timer);
    this.#timer = window.setTimeout(() => {
      if (error) error.hidden = true;
      this.#setState('idle');
    }, ERROR_MS);
  }

  /** @param {MouseEvent} event */
  #onClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (this.dataset.state === 'busy') return;

    const variantId = this.dataset.variantId;
    if (!variantId) return;
    const quantity = Math.max(1, Number(this.dataset.quantity) || 1);

    clearTimeout(this.#timer);
    const error = this.#errorEl;
    if (error) error.hidden = true;
    this.#setState('busy');

    // The cart drawer's items re-render from the HTML returned with the add, as in product-form.js
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    /** @type {Record<string, unknown>} */
    const body = { items: [{ id: Number(variantId), quantity }] };
    if (sectionIds.length) body.sections = [...new Set(sectionIds)].join(',');

    let added;
    try {
      const response = await fetch(`${routesRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(body),
      });
      added = await response.json();
      if (!response.ok || added?.status) {
        const message = typeof added?.description === 'string' && added.description ? added.description : ERROR_TEXT;
        this.dispatchEvent(new CartErrorEvent({ error: added?.message || 'Add to cart failed', code: 'INVALID', detail: { description: added?.description } }));
        this.#showError(message);
        return;
      }
    } catch (err) {
      console.error(err);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      this.#showError(ERROR_TEXT);
      return;
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const cartResponse = await fetch(`${routesRoot()}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'aq-quick-add',
          sourceId: variantId,
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: added?.sections,
          didError: false,
        },
      });
    } catch (err) {
      deferred.reject(err);
    }

    this.#setState('done');
    this.#timer = window.setTimeout(() => this.#setState('idle'), DONE_MS);
  };
}

if (!customElements.get('aq-quick-add')) customElements.define('aq-quick-add', AqQuickAdd);
